import { createPopper } from '@popperjs/core';
import Fuse from 'fuse.js';
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import phoneFlags from 'react-phone-number-input/flags';
import phoneCountryLabels from 'react-phone-number-input/locale/fr.json';
import 'react-phone-number-input/style.css';
import removeAccents from 'remove-accents';
import {
  Check,
  ChevronDown,
  CrossIcon,
  Import,
  NoShowPassword,
  Plus,
  ShowPassword,
  WarningCircle,
} from '../../assets/icons/index';
import { toPhoneValue } from 'helper/companyContactPhone';
import FieldLabel from './FieldLabel';

const FIELD_GROUP_CLASS = 'flex flex-col gap-1.5';
// Help text under a field: 12 px, as under the admin's other fields (crm/ui RichSelect).
const HINT_CLASS = 'text-xs font-normal text-faint';
// Text types pass no list. A shared constant, so the filtering effect does not
// see a new array on every render; an instance turned from text into a select
// in place (same position in the tree) must never map an undefined list.
const EMPTY_LIST = [];
const PLACEHOLDER_CLASS = 'placeholder:text-gray-400';

const StyledInput = ({
  label,
  type,
  unit,
  list,
  fileName,
  fileAccept,
  fileSize,
  placeholder,
  value,
  checked,
  onChange,
  // Autocomplete only: called with the chosen option ({ value, label }), as
  // two options may share a label and onChange only carries the label.
  onPick,
  // Forwarded to the text-like input, so a widget such as react-datepicker
  // can drive it through `customInput`.
  onClick,
  onFocus,
  onBlur,
  onKeyDown,
  error,
  // Help text under the field, dropped as soon as an error takes its place.
  hint,
  // Second line under a checkbox or radio label.
  detail,
  imgFront,
  imgBack,
  highlightContent,
  isSearchable,
  isSecondary,
  isOptional,
  isDisabled,
  step,
  className,
  classNameValue,
  additionalClassName,
  classNameDropDown,
  additionalClassNameDropDown,
  showChevron = true,
  showCheck = true,
  listSuggestions = [],
  onClickSuggestion = () => {},
  shouldKeepOpenOnSelect = false,
  dropdownFullWidth = false,
  footer,
  onSearchChange,
  visibleList,
  inputRef,
  // Textareas default to 500 characters; a text input is only capped when
  // the caller asks for it.
  maxLength,
  name,
  inputMode,
  // A text input that drives its own list (a combobox): forwarded as is.
  role,
  ariaExpanded,
  ariaControls,
  autoComplete,
}) => {
  const [typeInputChange, setTypeInputChange] = useState(type || 'text');
  const phoneId = useId();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredList, setFilteredList] = useState(list ?? EMPTY_LIST);
  const highlightRef = useRef(null);
  const selectMultipleRef = useRef();
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const dropdownOptionItemClassName =
    'flex gap-1 dropDown-item p-2 hover:bg-gray-100 rounded transition-colors cursor-pointer';

  const stopDropdownWheelPropagation = (event) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.style.height = 'auto';
      highlightRef.current.style.height = highlightRef.current.scrollHeight + 'px';
    }
  }, [value, highlightContent]);

  useEffect(() => {
    let handler = (e) => {
      if (
        selectMultipleRef.current &&
        !selectMultipleRef.current.contains(e.target)
      ) {
        setIsSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [selectMultipleRef]);

  useEffect(() => {
    if (
      (isSearchable || type === 'autocomplete') &&
      search &&
      list.length > 0 &&
      !onSearchChange
    ) {
      const searchTerms = search
        .split(' ')
        .map((term) => removeAccents(term.toLowerCase()));

      const options = {
        keys: ['searchLabel', 'label'],
        includeScore: true,
        threshold: 0.3,
        distance: 100,
        shouldSort: true,
        getFn: (obj, path) => {
          const value = Fuse.config.getFn(obj, path);
          if (value == null || typeof value !== 'string') {
            return '';
          }
          return removeAccents(value.toLowerCase());
        },
      };

      const fuse = new Fuse(list, options);
      const combinedResults = {};

      searchTerms.forEach((term) => {
        const result = fuse.search(term);
        result.forEach(({ item, score }) => {
          if (combinedResults[item.value]) {
            combinedResults[item.value].score -= score;
          } else {
            combinedResults[item.value] = { item, score };
          }
        });
      });

      const sortedResults = Object.values(combinedResults).sort(
        (a, b) => a.score - b.score
      );

      setFilteredList(sortedResults.map((result) => result.item));
    } else {
      setFilteredList(list ?? EMPTY_LIST);
    }
  }, [search, list, isSearchable, type, onSearchChange]);

  const dropdownList =
    visibleList !== undefined
      ? visibleList
      : filteredList;

  const deleteAllChecked = () => {
    onChange(list.map((item) => ({ ...item, checked: false })));
  };

  const handleKeyDown = (e) => {
    if (typeInputChange === 'number' && ['e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
    onKeyDown?.(e);
  };

  useEffect(() => {
    if (isSelectOpen && isSearchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSelectOpen, isSearchable]);

  useEffect(() => {
    if (isSelectOpen && buttonRef.current && dropdownRef.current) {
      const modifiers = [
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-start'],
          },
        },
      ];

      if (dropdownFullWidth && selectMultipleRef.current) {
        modifiers.push({
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${selectMultipleRef.current.offsetWidth}px`;
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${selectMultipleRef.current.offsetWidth}px`;
          },
        });
      }

      const popperInstance = createPopper(buttonRef.current, dropdownRef.current, {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers,
      });
      
      return () => {
        popperInstance.destroy();
      };
    }
  }, [isSelectOpen, dropdownFullWidth]);

  // Label and optional detail line of a checkbox or radio. In a dropdown
  // (isSecondary) the option keeps the list's plain text.
  const renderControlText = () => {
    if (!label && !detail) return null;
    const labelColorClassName = isDisabled ? 'text-gray-400' : 'text-gray-900';

    return (
      <div className={'flex flex-col min-w-0'}>
        {label && (
          <div
            className={`ft-sm flex gap-1 ${
              isSecondary ? '' : `font-medium ${labelColorClassName}`
            }`}
          >
            {label}
            {isOptional && <div className="text-gray-400"> - Optionnel</div>}
          </div>
        )}
        {detail && <div className={'ft-sm text-gray-500'}>{detail}</div>}
      </div>
    );
  };

  const errorLine = error && (
    <div className={'text-red-500 flex items-center gap-1 text-xs'}>
      <WarningCircle wh={16} color={'#EF4444'} />
      {error}
    </div>
  );

  return (
    <>
      {type === 'phone' && (
        // `onChange` receives the number (E.164, '' when emptied), not an event.
        // A French overseas number typed with France selected (0696…) comes
        // out with its own calling code (+596…), as the api stores it.
        <div className={`${FIELD_GROUP_CLASS} min-w-0 w-full ${className || ''}`}>
          <FieldLabel label={label} isOptional={isOptional} htmlFor={phoneId} />
          <PhoneInput
            // `international` stays unset so that a pasted "+590…" is kept as
            // well as a national number; `initialValueFormat` shows a stored
            // number in national format, the flag naming its country.
            defaultCountry={'FR'}
            initialValueFormat={'national'}
            flags={phoneFlags}
            labels={phoneCountryLabels}
            placeholder={placeholder}
            value={value || undefined}
            onChange={(next) => onChange(toPhoneValue(next) ?? next ?? '')}
            disabled={isDisabled}
            // The flag sits inside the field's box: the border goes on the
            // library's wrapper, not on its input.
            className={`flex items-center gap-2 h-10 border rounded-[0.4rem] px-3 focus-within:border-yellow-500 ${
              isDisabled ? 'bg-gray-100' : 'bg-white'
            }`}
            // The library derives these two from --PhoneInput-color--focus in
            // :root, where the value is already resolved: setting that
            // variable here would change nothing.
            style={{
              '--PhoneInputCountrySelectArrow-color--focus': '#ff9600',
              '--PhoneInputCountryFlag-borderColor--focus': '#ff9600',
            }}
            numberInputProps={{
              id: phoneId,
              className: `flex-1 min-w-0 h-full p-0 text-sm text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-0 ${PLACEHOLDER_CLASS}`,
            }}
          />
          {errorLine}
          {hint && !error && <div className={HINT_CLASS}>{hint}</div>}
        </div>
      )}
      {type === 'textarea' && (
        <label className={FIELD_GROUP_CLASS}>
          <FieldLabel label={label} isOptional={isOptional} />
          <div className={'relative'}>
            <textarea
              cols="30"
              rows="5"
              className={`w-full border rounded-[0.4rem] p-3 text-sm ${PLACEHOLDER_CLASS} focus:border-yellow-500 focus:outline-none ${
                isDisabled ? 'bg-gray-100' : ''
              }`}
              value={value}
              maxLength={maxLength ?? 500}
              minLength={3}
              placeholder={placeholder}
              onChange={onChange}
              disabled={isDisabled}
            />
          </div>
          {errorLine}
          {hint && !error && <div className={HINT_CLASS}>{hint}</div>}
        </label>
      )}
      {(type === 'text' ||
        type === 'password' ||
        type === 'number' ||
        type === 'time' ||
        type === 'email') && (
        <label className={`${FIELD_GROUP_CLASS} min-w-0 w-full ${className || ''}`}>
          <FieldLabel label={label} isOptional={isOptional} />
          <div className={'relative'}>
            {imgFront ? (
              <div
                className={
                  'absolute left-0 top-0 h-10 flex items-center p-1.5 z-[1]'
                }
              >
                {imgFront}
              </div>
            ) : null}
            {highlightContent !== undefined ? (
              <>
                <textarea
                  ref={highlightRef}
                  placeholder={placeholder}
                  value={value}
                  onChange={onChange}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  className={`w-full min-h-[2.5rem] border rounded-[0.4rem] px-3 py-2 text-sm leading-normal ${PLACEHOLDER_CLASS} focus:border-yellow-500 focus:outline-none resize-none overflow-hidden ${
                    imgFront ? 'pl-7' : ''
                  } ${imgBack ? 'pr-7' : ''} ${
                    isDisabled ? 'bg-gray-100' : ''
                  }`}
                  style={highlightContent ? { color: 'transparent', caretColor: '#111827' } : undefined}
                  disabled={isDisabled}
                />
                {highlightContent && (
                  <div
                    className={`absolute inset-0 px-3 py-2 text-sm leading-normal pointer-events-none whitespace-pre-wrap break-words ${
                      imgFront ? 'pl-7' : ''
                    } ${imgBack ? 'pr-7' : ''}`}
                  >
                    {highlightContent}
                  </div>
                )}
              </>
            ) : (
              <input
                ref={inputRef}
                type={typeInputChange}
                name={name}
                maxLength={maxLength}
                inputMode={inputMode}
                role={role}
                aria-expanded={ariaExpanded}
                aria-controls={ariaControls}
                autoComplete={autoComplete}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onClick={onClick}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={handleKeyDown}
                className={`w-full h-10 border rounded-[0.4rem] px-3 text-sm ${PLACEHOLDER_CLASS} focus:border-yellow-500 focus:outline-none ${
                  type === 'password' ? 'pr-7' : ''
                } ${
                  type === 'number'
                    ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                    : ''
                } ${unit ? 'pr-20' : ''} ${imgFront ? 'pl-7' : ''} ${
                  imgBack ? 'pr-7' : ''
                } ${isDisabled ? 'bg-gray-100' : ''}`}
                disabled={isDisabled}
                step={step}
                {...(type === 'number' && {
                  pattern: '[0-9]*',
                  inputMode: 'numeric',
                })}
                {...(type === 'email' && { inputMode: 'email' })}
              />
            )}
            {imgBack ? (
              <div
                className={
                  'absolute right-0 top-0 h-10 flex items-center p-1.5 z-[1]'
                }
              >
                {imgBack}
              </div>
            ) : null}
            {type === 'password' && (
              <>
                <div
                  className="absolute inset-y-0 right-0 flex items-center px-2 cursor-pointer"
                  onClick={() => {
                    if (typeInputChange === 'password')
                      setTypeInputChange('text');
                    if (typeInputChange === 'text')
                      setTypeInputChange('password');
                  }}
                >
                  {typeInputChange === 'text' ? (
                    <NoShowPassword wh={18} color={'#666666'} strokeW={2} />
                  ) : (
                    <ShowPassword wh={18} color={'#666666'} strokeW={2} />
                  )}
                </div>
              </>
            )}
            {unit && (
              <div
                className={'absolute inset-y-0 right-0 flex items-center pr-3'}
              >
                {unit}
              </div>
            )}
          </div>
          {errorLine}
          {hint && !error && <div className={HINT_CLASS}>{hint}</div>}
          {/* suggestions pour les champs texte */}
          {type === 'text' && listSuggestions.length > 0 && (
            <div className="flex gap-2">
              {listSuggestions.map((sugg, index) => (
                <button
                  key={index}
                  className="flex items-center justify-center gap-1 rounded text-gray-800 bg-gray-100 py-0.5 px-1.5"
                  onClick={() => onClickSuggestion(sugg)}
                >
                  <div className="flex items-center gap-0.5">
                    <Plus wh="16" color="#374151" />
                    {sugg.name}
                  </div>
                </button>
              ))}
            </div>
          )}
        </label>
      )}
      {(type === 'checkbox' || type === 'checkbox-round') && (
        <label
          className={`flex gap-3 items-center relative justify-between w-full ${
            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className={'flex gap-3 items-center relative min-w-0'}>
            <input
              type={'checkbox'}
              name={name}
              placeholder={placeholder}
              checked={checked}
              onChange={onChange}
              disabled={isDisabled}
              className={`checkbox-primary ${isSecondary ? 'hidden' : ''}`}
            />
            {renderControlText()}
          </div>
          {checked && isSecondary && (
            <div className={''}>
              <Check wh={16} color={'#374151'} />
            </div>
          )}
        </label>
      )}
      {type === 'radio' && (
        <label
          className={`flex gap-3 items-center relative justify-between w-full ${
            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className={'flex gap-3 items-center relative min-w-0'}>
            <input
              type={'radio'}
              name={name}
              placeholder={placeholder}
              checked={checked}
              onChange={onChange}
              disabled={isDisabled}
              className={`radio-primary ${isSecondary ? 'hidden' : ''}`}
            />
            {renderControlText()}
          </div>
          <div className={''}>
            {checked && isSecondary && <Check wh={16} color={'#374151'} />}
          </div>
        </label>
      )}
      {type === 'file' && (
        <label className={FIELD_GROUP_CLASS}>
          <FieldLabel label={label} isOptional={isOptional} />
          <label className="w-full flex flex-row  border border-gray-300 rounded-lg h-11 shadow-sm">
            <div className="ft-sm text-gray-700 p-4 flex items-center justify-center gap-2 border-r border-gray-300">
              <Import color={'#374151'} wh={14} />
              Importer
            </div>
            <div className="ft-sm truncate overflow-hidden text-gray-400 flex items-center ml-3">
              {fileName ? fileName : 'Aucun fichier'}
            </div>
            <input
              type={type}
              onChange={onChange}
              accept={fileAccept.map((type) => type.name).join(', ')}
              className="hidden"
            />
          </label>
          <div className={HINT_CLASS}>
            {fileAccept.length > 1
              ? 'Formats acceptés : '
              : 'Format accepté : '}
            {fileAccept.map((type) => type.title).join(', ')} ({fileSize} max)
          </div>
          {errorLine}
        </label>
      )}
      {type === 'autocomplete' && (
        <div
          className={`${FIELD_GROUP_CLASS} relative w-full`}
          ref={selectMultipleRef}
        >
          <FieldLabel label={label} isOptional={isOptional} />
          <div className={'relative'} ref={buttonRef}>
            {imgFront ? (
              <div
                className={
                  'absolute left-0 top-1/2 transform -translate-y-1/2 p-1.5'
                }
              >
                {imgFront}
              </div>
            ) : null}
            <input
              type={'text'}
              placeholder={placeholder}
              value={value}
              onChange={(e) => {
                setSearch(e.target.value);
                onChange(e);
              }}
              onFocus={() => setIsSelectOpen(true)}
              onBlur={() => setIsSelectOpen(false)}
              className={`w-full h-10 border rounded-[0.4rem] px-3 ${PLACEHOLDER_CLASS} focus:border-yellow-500 focus:outline-none ${
                imgFront ? 'pl-7' : ''
              } ${isDisabled ? 'bg-gray-100' : ''}`}
              disabled={isDisabled}
            />
            {isSelectOpen && (
            <div
              ref={dropdownRef}
              onWheel={stopDropdownWheelPropagation}
              className={
                'z-20 w-fit min-w-full bg-white border rounded shadow-2xl py-2 max-h-[170px] overflow-y-auto dropDown absolute left-0 mt-1 origin-top-left [&[data-popper-placement^=top]]:origin-bottom-left motion-safe:animate-menuIn'
              }
            >
                {filteredList && filteredList.length > 0 ? (
                  <div className={classNameDropDown ?? 'flex flex-col'}>
                    {filteredList.map((item, index) => (
                      <button
                        key={index}
                        type="button"
                        className={
                          'text-left px-3 py-2 hover:bg-gray-100 truncate'
                        }
                        onMouseDown={(e) => {
                          // use onMouseDown to avoid losing focus before click handler runs
                          e.preventDefault();
                          const syntheticEvent = {
                            target: { value: item.label },
                          };
                          setSearch(item.label);
                          onChange(syntheticEvent);
                          onPick?.(item);
                          setIsSelectOpen(false);
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={'px-3 py-2 text-gray-400'}>
                    Aucun résultat
                  </div>
                )}
              </div>
            )}
          </div>
          {errorLine}
          {hint && !error && <div className={HINT_CLASS}>{hint}</div>}

          {listSuggestions.length > 0 && (
            <div className="flex gap-2">
              {listSuggestions.map((sugg, index) => (
                <button
                  key={index}
                  className="flex items-center justify-center gap-1 rounded text-gray-800 bg-gray-100 py-0.5 px-1.5"
                  onClick={() => {
                    const newVal = sugg.name ?? sugg.label ?? '';
                    const syntheticEvent = { target: { value: newVal } };
                    setSearch(newVal);
                    onChange(syntheticEvent);
                    onClickSuggestion(sugg);
                    setIsSelectOpen(false);
                  }}
                >
                  <div className="flex items-center gap-0.5">
                    <Plus wh="16" color="#374151" />
                    {sugg.name ?? sugg.label}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {type === 'select' && (
        <label className={FIELD_GROUP_CLASS}>
          <FieldLabel label={label} isOptional={isOptional} />
          <div className={'relative'}>
            {imgFront ? (
              <div
                className={
                  'absolute left-0 top-1/2 transform -translate-y-1/2 p-1.5'
                }
              >
                {imgFront}
              </div>
            ) : null}
            <select
              value={value ?? ''}
              onChange={onChange}
              disabled={isDisabled}
              className={`w-full h-10 truncate border rounded-[0.4rem] px-3 ft-sm text-gray-900 focus:border-yellow-500 focus:outline-none ${
                imgFront ? 'pl-7' : ''
              } ${isDisabled ? 'bg-gray-100' : ''}`}
            >
              {placeholder &&
                !filteredList.some((item) => item.value === '') && (
                  <option value="" disabled>
                    {placeholder}
                  </option>
                )}
              {filteredList.map((item, index) => (
                <option key={index} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          {errorLine}
          {/* Guidance on the value chosen, under the field. Never shown
              alongside an error: a correction comes before an explanation. */}
          {hint && !error && <div className={HINT_CLASS}>{hint}</div>}
        </label>
      )}
      {type === 'selectMultipleCheckbox' && (
        <div
          className={`${FIELD_GROUP_CLASS} relative w-full cursor-pointer`}
          ref={selectMultipleRef}
        >
          <FieldLabel label={label} isOptional={isOptional} />
          <div
            ref={buttonRef}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsSelectOpen(!isSelectOpen);
            }}
            onChange={onChange}
            className={`${
              className ??
              'w-full h-10 flex items-center border rounded-[0.4rem] bg-white text-gray-900 ft-sm'
            } ${additionalClassName || ''} hover:bg-gray-100 transition-colors`}
          >
            <div className={'flex justify-between items-center w-full min-w-0 overflow-hidden'}>
              <div className={'flex flex-1 min-w-0 items-center overflow-hidden'}>
                {showCheck &&
                  filteredList &&
                  filteredList.some((item) => item.checked) && (
                    <div className={'flex shrink-0 justify-end px-1 py-1.5'}>
                      <Check wh={20} color={'#374151'} />
                    </div>
                  )}
                <div
                  className={`min-w-0 flex-1 overflow-hidden ${classNameValue ?? 'px-3 py-2 flex items-center justify-start'} ${
                    showChevron
                      ? filteredList &&
                        filteredList.some((item) => item.checked)
                        ? 'pl-1'
                        : 'pr-1'
                      : ''
                  }`}
                >
                  <span className="min-w-0 flex-1 basis-0 truncate text-left ft-sm">
                    {value}
                  </span>
                </div>
              </div>
              {filteredList &&
                filteredList.every((item) => !item.checked && showChevron) && (
                  <div className={'flex shrink-0 justify-end px-1 py-2 pr-3'}>
                    <ChevronDown wh={20} color={'#374151'} />
                  </div>
                )}
              {filteredList && filteredList.some((item) => item.checked) && (
                <div
                  className={'flex shrink-0 justify-end px-1 py-2 pr-3'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteAllChecked();
                  }}
                >
                  <CrossIcon wh={18} color={'#374151'} />
                </div>
              )}
            </div>
          </div>
          {isSelectOpen && (
            <div
              ref={dropdownRef}
              onWheel={stopDropdownWheelPropagation}
              className={
                'z-[9999] w-fit min-w-[155px] bg-white border rounded shadow-2xl max-h-[170px] overflow-y-auto dropDown origin-top-left [&[data-popper-placement^=top]]:origin-bottom-left motion-safe:animate-menuIn'
              }
            >
              <div className={'flex flex-col gap-1 py-2 px-1'}>
                {isSearchable && (
                  <div className="px-1 py-2">
                    <StyledInput
                      type={'text'}
                      placeholder={'Rechercher...'}
                      value={search}
                      inputRef={searchInputRef}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        onSearchChange?.(e.target.value);
                      }}
                    />
                  </div>
                )}
                <div
                  className={`${classNameDropDown ?? 'flex flex-col gap-1'} ${
                    additionalClassNameDropDown || ''
                  }`}
                >
                  {dropdownList.map((item, index) => (
                    <div key={index} className={dropdownOptionItemClassName}>
                      <StyledInput
                        label={item.label}
                        type={'checkbox'}
                        isSecondary
                        checked={item.checked}
                        onChange={(e) => {
                          onChange(
                            list.map((i) =>
                              i.value === item.value
                                ? { ...i, checked: e.target.checked }
                                : i
                            )
                          );
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              {footer && (
                <div className="sticky bottom-0 rounded-b border-t border-gray-100 bg-white px-3 py-2">
                  {footer}
                </div>
              )}
            </div>
          )}
          {errorLine}
          {hint && !error && <div className={HINT_CLASS}>{hint}</div>}
        </div>
      )}
      {type === 'selectMultipleRadio' && (
        <div
          className={`${FIELD_GROUP_CLASS} relative w-full cursor-pointer`}
          ref={selectMultipleRef}
        >
          <FieldLabel label={label} isOptional={isOptional} />
          <div
            ref={buttonRef}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsSelectOpen(!isSelectOpen);
            }}
            onChange={onChange}
            className={`${
              className ??
              'w-full h-10 flex items-center border rounded-[0.4rem] bg-white text-gray-900 ft-sm'
            } ${additionalClassName || ''} hover:bg-gray-100 transition-colors`}
          >
            <div className={'flex justify-between items-center w-full min-w-0 overflow-hidden'}>
              <div className={'flex flex-1 min-w-0 items-center overflow-hidden'}>
                {showCheck &&
                  filteredList &&
                  filteredList.some((item) => item.checked) && (
                    <div className={'flex shrink-0 justify-end px-1 py-1.5'}>
                      <Check wh={20} color={'#374151'} />
                    </div>
                  )}
                <div
                  className={`min-w-0 flex-1 overflow-hidden ${classNameValue ?? 'px-3 py-2 flex items-center justify-start'} ${
                    showChevron
                      ? filteredList?.some((item) => item.checked)
                        ? 'pl-1'
                        : 'pr-1'
                      : ''
                  }`}
                >
                  <span className="min-w-0 flex-1 basis-0 truncate text-left ft-sm">
                    {value}
                  </span>
                </div>
              </div>
              {filteredList &&
                filteredList.every((item) => !item.checked && showChevron) && (
                  <div className={'flex shrink-0 justify-end px-3 py-2'}>
                    <ChevronDown wh={20} color={'#374151'} />
                  </div>
                )}
              {filteredList && filteredList.some((item) => item.checked) && (
                <div
                  className={'flex shrink-0 justify-end px-1 py-2 pr-3'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteAllChecked();
                  }}
                >
                  <CrossIcon wh={18} color={'#374151'} />
                </div>
              )}
            </div>
          </div>
          {isSelectOpen && (
            <div
              ref={dropdownRef}
              onWheel={stopDropdownWheelPropagation}
              className={
                'z-[9999] w-fit min-w-[155px] bg-white border rounded shadow-2xl dropDown flex flex-col max-h-[280px] origin-top-left [&[data-popper-placement^=top]]:origin-bottom-left motion-safe:animate-menuIn'
              }
            >
              <div
                onWheel={stopDropdownWheelPropagation}
                className={'flex flex-col gap-1 py-2 px-1 min-h-0 flex-1 overflow-y-auto dropDown'}
              >
                {isSearchable && (
                  <div className="px-1 py-2">
                    <StyledInput
                      type={'text'}
                      placeholder={'Rechercher...'}
                      value={search}
                      inputRef={searchInputRef}
                      onChange={(e) => {
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                )}
                <div
                  className={`${classNameDropDown ?? 'flex flex-col gap-1'} ${
                    additionalClassNameDropDown || ''
                  }`}
                >
                  {filteredList.map((item, index) => (
                    <Fragment key={index}>
                      {/* An option carrying a groupLabel opens its group the
                          first time it appears. The header follows the
                          filtered list, so a search that empties a group
                          takes its title away with it. */}
                      {item.groupLabel &&
                        item.groupLabel !== filteredList[index - 1]?.groupLabel && (
                          <div
                            className={
                              'px-3 pt-2 pb-1 text-xs uppercase tracking-wide text-gray-400'
                            }
                          >
                            {item.groupLabel}
                          </div>
                        )}
                    <div
                      className={dropdownOptionItemClassName}
                      onMouseDown={(e) => {
                        if (!item.checked) return;
                        e.preventDefault();
                        onChange(list.map((i) => ({ ...i, checked: false })));
                        if (!shouldKeepOpenOnSelect) {
                          setIsSelectOpen(false);
                        }
                      }}
                    >
                      <StyledInput
                        label={item.label}
                        type={'radio'}
                        isSecondary
                        checked={item.checked}
                        onChange={() => {
                          onChange(
                            list.map((i) =>
                              i.value === item.value
                                ? { ...i, checked: true }
                                : { ...i, checked: false }
                            )
                          );
                          if (!shouldKeepOpenOnSelect) {
                            setIsSelectOpen(false);
                          }
                        }}
                      />
                    </div>
                    </Fragment>
                  ))}
                </div>
              </div>
              {footer && (
                <div className="sticky bottom-0 rounded-b border-t border-gray-100 bg-white px-3 py-2">
                  {footer}
                </div>
              )}
            </div>
          )}
          {errorLine}
          {hint && !error && <div className={HINT_CLASS}>{hint}</div>}
          {listSuggestions.length > 0 && (
            <div className="flex gap-2">
              {listSuggestions.map((sugg, index) => (
                <button
                  key={index}
                  className="flex items-center justify-center gap-1 rounded text-gray-800 bg-gray-100 py-0.5 px-1.5"
                  onClick={() => onClickSuggestion(sugg)}
                >
                  <div className="flex items-center gap-0.5">
                    <Plus wh="16" color="#374151" />
                    {sugg.name}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default StyledInput;
